<script setup lang="ts">
import { SelectOption } from "@/models/frontend-only/SelectOption";

const props = withDefaults(
  defineProps<{
    label?: string;
    options?: (SelectOption | string)[];
    modelValue?: string;
    id?: string;
    hideLabel?: boolean;
    inline?: boolean;
    disabled?: boolean;
    disabledPlaceholder?: string;
    smIcon?: string;
    small?: boolean;
    placeholder?: boolean;
  }>(),
  {
    label: "",
    modelValue: "",
    options: () => [],
    id: () => uniqueId("form-select"),
    hideLabel: false,
    inline: false,
    disabled: false,
    small: false,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const value = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit("update:modelValue", newValue);
  },
});

const fullOptions = computed<SelectOption[]>(() => {
  return props.options.map((o) => {
    if (typeof o !== "string") {
      return o;
    } else {
      return {
        value: o,
        label: o,
      };
    }
  });
});
</script>

<template>
  <div
    v-if="!placeholder"
    class="form-control"
    :class="{ inline: inline }"
  >
    <label
      :for="id"
      class="label"
      :class="{ 'sr-only': hideLabel }"
    >
      <span
        class="label-text"
        :class="{ 'd-none d-lg-block': smIcon }"
        >{{ label }}</span
      >
      <FAIcon
        v-if="smIcon"
        :class="`${smIcon} fa-fw fa-light d-lg-none`"
      />
    </label>
    <select
      :id="id"
      v-model="value"
      class="select"
      :class="{ 'form-select-sm': small }"
      :disabled="disabled"
    >
      <option
        v-if="disabledPlaceholder"
        selected
        value=""
      >
        {{ disabledPlaceholder }}
      </option>
      <option
        v-for="(o, i) of fullOptions"
        :key="i"
        :value="o.value"
      >
        {{ o.label }}
      </option>
    </select>
    <slot name="footer"></slot>
  </div>
  <div
    v-else
    class="form-control"
    :class="{ inline: inline }"
  >
    <label
      :for="id"
      class="label"
      :class="{ 'sr-only': hideLabel }"
    >
      <span
        class="label-text"
        :class="{ 'd-none d-lg-block': smIcon }"
        ><span
          class="placeholder"
          style="width: 75px"
      /></span>
    </label>
    <span
      :id="id"
      class="select"
      :class="{ 'form-select-sm': small }"
    >
      <span style="width: 100%; height: 1em" />
    </span>
    <slot name="footer"></slot>
  </div>
</template>

<style scoped lang="postcss">
label {
  @apply whitespace-nowrap text-gray-500;
}
.form-control.inline {
  @apply flex flex-row flex-nowrap gap-1;
}
</style>
